import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { omit } from 'remeda';
import { string, z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

export const LightUserSchema = z
  .object({
    id: z.number(),
    firstName: string().nonempty(),
    lastName: string().nonempty(),
    email: z.email(),
    isActive: z.boolean(),
    profilePicture: z.url().nullable(),
  })
  .transform(data => ({
    ...omit(data, ['firstName', 'lastName']),
    name: {
      full: `${data.firstName} ${data.lastName}`,
      first: data.firstName,
      last: data.lastName,
      initials: `${data.firstName[0]}${data.lastName[0]}`,
    },
  }));

const LightUsersSchema = z.array(LightUserSchema);

export type LightUser = z.infer<typeof LightUserSchema>;
type LightUsers = z.infer<typeof LightUsersSchema>;

type LightUsersVariables = {
  query: string;
};

export async function loadLightUsersQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get(
    `/users?${new URLSearchParams({
      fullResponse: 'false',
      showInactive: queryKey[1].loadInactiveUsers.toString(),
    }).toString()}`,
  );

  return LightUsersSchema.parse(data);
}

const emptyUsers: LightUsers = [];

export const useLoadLightUsers = (
  loadInactiveUsers: boolean = false,
  variables: LightUsersVariables = { query: '' },
  options?: { isEnabled?: boolean },
) => {
  const { isEnabled = true } = options ?? {};
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<LightUsers, ApiError>({
    queryKey: ['users', { ...variables, loadInactiveUsers }] as const,
    queryFn: context => loadLightUsersQueryFn(axiosInstance, context),
    placeholderData: data => keepPreviousData(data) ?? emptyUsers,
    enabled: isEnabled,
  });

  return addResourceNameToQueryResult<LightUsers, unknown, 'users', 'PreventUndefined'>(
    'users',
    queryResult,
    emptyUsers,
  );
};
